/**
 * wordSync — imperative word-level karaoke highlight controller.
 *
 * Implementation shape: module-level singleton that manipulates DOM
 * classList directly, bypassing React state entirely. This is the
 * correct shape for the word layer because:
 *
 *   - onTimeUpdate fires ~20x/sec during playback
 *   - We must NOT re-render all TurnRows per tick — React.memo on TurnRow
 *     is load-bearing for perf (600+ turns)
 *   - The active PARAGRAPH already triggers a React state update via
 *     setActiveTurnIdx (rAF loop in TranscriptView) — that's the only
 *     per-frame state change allowed
 *   - The word highlight within the ACTIVE paragraph is a sub-render
 *     decoration: one span at a time, changing ~2-5x/sec — perfect for
 *     direct classList manipulation
 *
 * Lifecycle contract (2026-06-12 live-bug fix):
 *   - RawTokens registers each token span via a callback ref (el) and
 *     deregisters on the null call. There is NO separate "commit" step
 *     required: the controller keeps a Map keyed by globalIdx and lazily
 *     re-sorts on the next updateMs() after any registration change
 *     (dirty flag). This makes the controller robust to React's real
 *     lifecycle — refs re-firing on every TurnRow re-render (isActive
 *     flips during playback!), effects running child-before-parent,
 *     partial unmounts (edit mode swaps a turn's spans out), etc.
 *   - The current highlight is tracked by ELEMENT, not array index, so
 *     a re-sort never orphans or flickers the lit word.
 *   - resetAll() exists for tests; production code must NOT call it from
 *     a parent effect — parent effects run AFTER child registrations and
 *     would wipe them (the original live bug: zero highlights because
 *     TranscriptView's resetAll effect ran after all spans registered).
 */

/** A registered token span (global token index → DOM element + timing). */
export interface TokenSpanEntry {
  globalIdx: number;
  startMs: number;
  endMs: number;
  el: HTMLSpanElement;
}

const PLAYING_WORD_CLASS = "is-playing-word";

class WordSyncController {
  /** Registry keyed by global token index (unique across the transcript). */
  private byIdx = new Map<number, TokenSpanEntry>();
  /** Sorted-by-startMs view of the registry, rebuilt lazily. */
  private sorted: TokenSpanEntry[] = [];
  /** True when byIdx changed since the last sort. */
  private dirty = false;
  /** The element currently carrying the highlight class (null = none). */
  private currentEl: HTMLSpanElement | null = null;

  /** Register (or replace) a token span element. */
  register(entry: TokenSpanEntry): void {
    this.byIdx.set(entry.globalIdx, entry);
    this.dirty = true;
  }

  /**
   * Remove a single span (called when its callback ref fires with null).
   * Identity-guarded: if a NEW span already re-registered under the same
   * globalIdx (transcript reload reshuffles segments — the old span's null
   * ref can fire AFTER the new span's register), only remove the entry when
   * it still belongs to the deregistering element.
   */
  deregister(globalIdx: number, el?: HTMLSpanElement): void {
    const entry = this.byIdx.get(globalIdx);
    if (!entry) return;
    if (el && entry.el !== el) return; // a newer span owns this index now
    if (entry.el === this.currentEl) {
      this.currentEl.classList.remove(PLAYING_WORD_CLASS);
      this.currentEl = null;
    }
    this.byIdx.delete(globalIdx);
    this.dirty = true;
  }

  /**
   * Force a re-sort now. Optional — updateMs() sorts lazily on its own.
   * Kept for tests and for callers that want deterministic state.
   */
  commit(): void {
    this.ensureSorted();
  }

  /** Clear all spans + highlight. Test helper; see lifecycle contract above. */
  resetAll(): void {
    this.currentEl?.classList.remove(PLAYING_WORD_CLASS);
    this.currentEl = null;
    this.byIdx.clear();
    this.sorted = [];
    this.dirty = false;
  }

  /** Remove the highlight from whichever span is currently lit. */
  clearWordHighlight(): void {
    this.currentEl?.classList.remove(PLAYING_WORD_CLASS);
    this.currentEl = null;
  }

  /**
   * Update the word highlight for the given playback position (ms).
   * Called every rAF frame while the playhead is inside a turn; does
   * its own change-detection (element identity) so repeat calls within
   * the same token are free.
   * Returns the sorted-array index of the lit span (-1 if none).
   */
  updateMs(ms: number): number {
    this.ensureSorted();
    const idx = findTokenIndexAtMs(this.sorted, ms);
    let el = idx >= 0 ? this.sorted[idx].el : null;
    // Never decorate a detached node (a stale entry can exist for one commit
    // during a transcript reload, between cleanup and the null-ref sweep).
    if (el && !el.isConnected) el = null;
    if (el === this.currentEl) return idx; // no change — the common case

    this.currentEl?.classList.remove(PLAYING_WORD_CLASS);
    el?.classList.add(PLAYING_WORD_CLASS);
    this.currentEl = el;
    return idx;
  }

  /** Sorted view of the registry (test helper). */
  getSpans(): readonly TokenSpanEntry[] {
    this.ensureSorted();
    return this.sorted;
  }

  /** Sorted-array index of the currently lit span (-1 = none). Test helper. */
  getCurrentIdx(): number {
    if (!this.currentEl) return -1;
    this.ensureSorted();
    const cur = this.currentEl;
    return this.sorted.findIndex((s) => s.el === cur);
  }

  private ensureSorted(): void {
    if (!this.dirty) return;
    this.sorted = Array.from(this.byIdx.values()).sort(
      (a, b) => a.startMs - b.startMs,
    );
    this.dirty = false;
  }
}

/**
 * findTokenIndexAtMs — binary search for the token active at `ms`.
 *
 * Finds the largest index i where spans[i].startMs <= ms, then verifies
 * ms < spans[i].endMs. Returns -1 when ms is before the first token,
 * after the last token, or in a gap between tokens.
 *
 * Exported for unit testing.
 */
export function findTokenIndexAtMs(
  spans: readonly { startMs: number; endMs: number }[],
  ms: number,
): number {
  if (spans.length === 0) return -1;

  let lo = 0;
  let hi = spans.length - 1;
  let candidate = -1;

  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    if (spans[mid].startMs <= ms) {
      candidate = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  if (candidate < 0) return -1;
  // Verify ms is still within the candidate token's end boundary.
  // Gaps between tokens (endMs[i] < ms < startMs[i+1]) return -1.
  if (ms >= spans[candidate].endMs) return -1;
  return candidate;
}

/** Module-level singleton. */
export const wordSync = new WordSyncController();
